import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { useTranslation } from 'react-i18next';

interface ImageFolder {
  id: string;
  name: string;
  color?: string;
  imageCount?: number;
  createdAt: string;
  createdBy?: string;
}

interface FolderEditModalProps {
  open: boolean;
  folder: ImageFolder | null;
  onClose: () => void;
  onUpdate: (folderId: string, name: string, color: string) => Promise<void>;
}

const FolderEditModal: React.FC<FolderEditModalProps> = ({
  open,
  folder,
  onClose,
  onUpdate
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#2196f3');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Folder değiştiğinde form alanlarını güncelle
  useEffect(() => {
    if (folder) {
      setFolderName(folder.name);
      setFolderColor(folder.color || '#2196f3');
      setError(null);
    }
  }, [folder]);

  // Modal kapanırken temizle
  const handleClose = () => {
    if (isUpdating) return;
    setError(null);
    onClose();
  };

  // Klasör güncelle
  const handleUpdate = async () => {
    if (!folder) return;

    if (!folderName.trim()) {
      setError(tt('Klasör adı boş bırakılamaz', 'Folder name cannot be empty'));
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      await onUpdate(folder.id, folderName.trim(), folderColor);
      
      // State güncellemesinin tamamlanması için kısa bir gecikme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      handleClose();
    } catch (error: any) {
      console.error('Klasör güncelleme hatası:', error);
      // Backend'den gelen hata mesajını göster
      const errorMessage = error?.message || tt('Klasör güncellenirken bir hata oluştu', 'An error occurred while updating folder');
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Enter tuşuna basıldığında güncelle
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isUpdating) {
      handleUpdate();
    }
  };

  if (!folder) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FolderIcon sx={{ color: folderColor }} />
          <Typography variant="h6">
            {tt('Klasör Düzenle', 'Edit Folder')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Hata mesajı */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Klasör adı */}
          <TextField
            autoFocus
            fullWidth
            label={tt('Klasör Adı', 'Folder Name')}
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isUpdating}
            margin="normal"
            placeholder={tt('Örn: Ürün Görselleri', 'Ex: Product Images')}
            inputProps={{ maxLength: 100 }}
          />

          {/* Renk seçici */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {tt('Klasör Rengi', 'Folder Color')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {[
                '#2196f3', // Mavi
                '#4caf50', // Yeşil
                '#ff9800', // Turuncu
                '#f44336', // Kırmızı
                '#9c27b0', // Mor
                '#00bcd4', // Cyan
                '#795548', // Kahverengi
                '#607d8b', // Gri
                '#e91e63', // Pembe
                '#ffeb3b', // Sarı
              ].map((color) => (
                <Box
                  key={color}
                  onClick={() => !isUpdating && setFolderColor(color)}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 1,
                    cursor: isUpdating ? 'not-allowed' : 'pointer',
                    border: folderColor === color ? '3px solid #000' : '2px solid #ddd',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: isUpdating ? 'none' : 'scale(1.1)',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Klasör bilgileri */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{tt('Resim Sayısı:', 'Image Count:')}</strong> {folder.imageCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>{tt('Oluşturulma:', 'Created:')}</strong> {new Date(folder.createdAt).toLocaleString(localeCode)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={isUpdating}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={isUpdating || !folderName.trim()}
          startIcon={isUpdating ? <CircularProgress size={20} /> : null}
        >
          {isUpdating ? tt('Güncelleniyor...', 'Updating...') : tt('Güncelle', 'Update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderEditModal;
